import React, { useState, useEffect, useRef } from "react";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import FormField from "../../components/Form/FormField";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../app/store";
import {
  createSurvey,
  getSurveyDetails,
  updateSurveyById,
} from "../../app/surveySlice";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { fetchProjectDropdownList } from "../../services/projectServices";
import {
  fetchMemberList,
  fetchUserDropdownList,
} from "../../services/authServices";
import { Member } from "../../types/surveyTypes";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import "../../components/datepicker.scss";
import { Calendar } from "lucide-react";
interface Project {
  id: number;
  name?: string;
  start?: string;
  end?: string;
}
interface User {
  id: number;
  name?: string;
}
const PAGE_SIZE = 50;
const CreateSurvey = () => {
  const startDateRef = useRef<DatePicker>(null);
  const endDateRef = useRef<DatePicker>(null);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const survey = location.state?.survey;
  const initialFormData = {
    id: "",
    project_id: "",
    start_time: "",
    end_time: "",
    title: "",
    status: "active",
    reportingManager: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [manager, setManager] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<Member[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [memberDefaultCheckedMap, setMemberDefaultCheckedMap] = useState<
    Record<number, boolean>
  >({});
  const [allMemberIds, setAllMemberIds] = useState<number[]>([]);
  const [manualCheckedIds, setManualCheckedIds] = useState<
    Record<number, boolean>
  >({});
  const selectedProject = projects.find(
    (p) => p.id.toString() === formData.project_id
  );
  useEffect(() => {
    (async () => {
      try {
        const [userResult, projectResult] = await Promise.all([
          fetchUserDropdownList(),
          fetchProjectDropdownList(),
        ]);
        setManager(userResult);
        setProjects(projectResult);
      } catch (err: any) {
        toast.error(err.message || "Failed to load dropdown data");
      }
    })();
  }, []);
  useEffect(() => {
    const surveyId = location.state?.id;
    if (surveyId) {
      (async () => {
        try {
          const res = await dispatch(
            getSurveyDetails({ id: surveyId })
          ).unwrap();
          const surveyData = res.data;
          setFormData({
            id: surveyData.id?.toString() || "",
            project_id: surveyData.project_id?.toString() || "",
            start_time: surveyData.start
              ? new Date(surveyData.start).toISOString().slice(0, 10)
              : "",
            end_time: surveyData.end
              ? new Date(surveyData.end).toISOString().slice(0, 10)
              : "",
            title: surveyData.name || "",
            status: surveyData.status || "active",
            reportingManager: surveyData.reporting_manager_id?.toString() || "",
          });
          // Initialize manualCheckedIds for editing - mark surveyData.member_ids as checked (true)
          if (surveyData.all_member === 1 || surveyData.all_member === "1") {
            // all selected => clear manual overrides (means all are checked)
            setManualCheckedIds({});
          } else {
            // only these members selected
            const map: Record<number, boolean> = {};
            (surveyData.member_ids || []).forEach((id: number) => {
              map[id] = true;
            });
            setManualCheckedIds(map);
          }
        } catch (err: any) {
          toast.error(err.message || "Failed to fetch survey details");
        }
      })();
    } else {
      setFormData(initialFormData);
      setErrors({});
      setManualCheckedIds({});
    }
  }, [location.state?.id, dispatch]);

  useEffect(() => {
    const fetchAllMembers = async () => {
      if (!formData.reportingManager) {
        setAllUsers([]);
        setTotalMembers(0);
        setAllMemberIds([]);
        return;
      }
      try {
        const resp = await fetchMemberList({
          search: debouncedSearch, // or empty string if you want no search for full list
          reporting_manager_id: Number(formData.reportingManager),
          page: 1,
          pagesize: 10000,
          survey_id: Number(formData.id),
        });
        const allMembers = resp.data.data;
        const allIds = allMembers.map((m: Member) => m.id);
        setAllMemberIds(allIds);

        const defaultCheckedMap: Record<number, boolean> = {};
        allMembers.forEach((m: Member) => {
          const isChecked =
            typeof m.is_checked === "string"
              ? parseInt(m.is_checked, 10) === 1
              : m.is_checked === 1;
          defaultCheckedMap[m.id] = isChecked;
        });
        setMemberDefaultCheckedMap(defaultCheckedMap);
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch all members");
      }
    };

    fetchAllMembers();
  }, [formData.reportingManager, formData.id]);
  // This runs only when reportingManager or survey id changes

  useEffect(() => {
    const fetchPagedMembers = async () => {
      if (!formData.reportingManager) {
        setAllUsers([]);
        setTotalMembers(0);
        return;
      }
      try {
        const resp = await fetchMemberList({
          search: debouncedSearch,
          reporting_manager_id: Number(formData.reportingManager),
          page: currentPage,
          pagesize: PAGE_SIZE,
          survey_id: Number(formData.id),
        });

        const pagedMembers: Member[] = resp.data.data.map((m: Member) => ({
          ...m,
          is_checked:
            typeof m.is_checked === "string"
              ? parseInt(m.is_checked, 10)
              : m.is_checked || 0,
        }));
        setAllUsers(pagedMembers);
        setTotalMembers(resp.data.total);
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch paged members");
      }
    };
    fetchPagedMembers();
  }, [formData.reportingManager, debouncedSearch, currentPage, formData.id]);

  const isUserChecked = (userId: number) => {
    if (userId in manualCheckedIds) return manualCheckedIds[userId];
    return memberDefaultCheckedMap[userId] ?? false;
  };

  const handleCheckboxChange = (userId: number) => {
    setManualCheckedIds((prev) => {
      // Determine current value from either manual state or default map
      const currentVal =
        userId in prev
          ? prev[userId]
          : memberDefaultCheckedMap[userId] ?? false;

      return {
        ...prev,
        [userId]: !currentVal, // toggle
      };
    });
  };

  const handleSelectAllChange = (checked: boolean) => {
    const newManualChecks: Record<number, boolean> = {};

    // Only override if the desired state is different from default
    allMemberIds.forEach((id) => {
      const currentDefault = memberDefaultCheckedMap[id] ?? false;
      if (currentDefault !== checked) {
        newManualChecks[id] = checked;
      }
    });

    setManualCheckedIds(newManualChecks);
  };

  const isSelectAllChecked =
    allMemberIds.length > 0 &&
    allMemberIds.every((id) => isUserChecked(id) === true);
  const totalPages = Math.ceil(totalMembers / PAGE_SIZE);
  const getSelectedMemberIds = (): number[] => {
    return allMemberIds.filter((id) => {
      if (id in manualCheckedIds) {
        return manualCheckedIds[id];
      }
      return memberDefaultCheckedMap[id] ?? false;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.trimStart() }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const selectedCount = allMemberIds.reduce((count, id) => {
    if (id in manualCheckedIds) {
      return count + (manualCheckedIds[id] ? 1 : 0);
    } else {
      return count + (memberDefaultCheckedMap[id] ? 1 : 0);
    }
  }, 0);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.project_id) newErrors.project_id = "Project is required";
    if (!formData.start_time) newErrors.start_time = "Start Date is required";
    if (!formData.title?.trim()) newErrors.title = "Survey Name is required";
    if (!formData.status) newErrors.status = "Status is required";
    if (!formData.reportingManager)
      newErrors.reportingManager = "Reporting Manager is required";
    // End date validation
    if (formData.start_time && formData.end_time) {
      const start = new Date(formData.start_time);
      const end = new Date(formData.end_time);
      if (end < start) {
        newErrors.end_time = "End date cannot be before start date";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChangeValue = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("project_id", formData.project_id);
      form.append("name", formData.title);
      form.append("start", new Date(formData.start_time).toISOString());
      if (formData.end_time)
        form.append("end", new Date(formData.end_time).toISOString());
      form.append("status", formData.status);
      form.append("reporting_manager_id", formData.reportingManager);
      const selectedIds = getSelectedMemberIds();
      const allSelected = selectedIds.length === allMemberIds.length;

      form.append("all_member", allSelected ? "1" : "0");
      if (!allSelected) {
        form.append("member_ids", selectedIds.join(","));
      }

      let result;
      if (formData.id) {
        // Update
        result = await dispatch(
          updateSurveyById({ id: Number(formData.id), formData: form })
        ).unwrap();
      } else {
        // Create
        result = await dispatch(createSurvey(form)).unwrap();
      }
      toast.success(
        result.message ||
          (formData.id
            ? "Survey updated successfully!"
            : "Survey created successfully!")
      );
      navigate("/dashboard/surveyListing");
      setLoading(false);
      setFormData(initialFormData);
      setManualCheckedIds({});
    } catch (error: any) {
      toast.error(error || "Failed to save survey");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageName={survey ? "Update Survey" : "Create Survey"} />
      <div className="bg-white dark:bg-boxdark p-6 rounded-md shadow-md max-w-7xl mx-auto">
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="col-span-1">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Select Project <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                className={`appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                  errors.project_id
                    ? "border-red-500"
                    : "border-stroke dark:border-form-strokedark"
                }`}
              >
                <option value="" disabled hidden>-- Select Project --</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {errors.project_id && (
              <p className="text-red-500 text-sm mt-1">{errors.project_id}</p>
            )}
          </div>
          <FormField
            label="Survey Name"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={errors.title}
            required
            maxLength={100}
          />
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Start <span className="text-red-500 ml-1">*</span>
            </label>
            <div
              className="w-full rounded-lg border py-2 px-4 flex items-center justify-between transition duration-300 focus-within:ring-1 cursor-pointer dark:bg-form-input dark:text-white"
              //onClick={() => startDateRef.current?.setOpen(true)}
            >
              <DatePicker
                ref={startDateRef}
                selected={
                  formData.start_time ? new Date(formData.start_time) : null
                }
                onChange={(date: Date | null) =>
                  handleChangeValue(
                    "start_time",
                    date ? format(date, "yyyy-MM-dd") : ""
                  )
                }
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                className="w-full bg-transparent outline-none"
                minDate={
                  selectedProject?.start
                    ? new Date(selectedProject.start)
                    : undefined
                }
                maxDate={
                  selectedProject?.end
                    ? new Date(selectedProject.end)
                    : undefined
                }
                disabled={!formData.project_id}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                openToDate={
                  selectedProject?.start
                    ? new Date(selectedProject.start)
                    : new Date()
                }
              />
              <Calendar className="w-5 h-5 text-gray-400 ml-2" />
            </div>
            {errors.start_time && (
              <p className="text-red-500 text-sm mt-1">{errors.start_time}</p>
            )}
          </div>
          {/* End Date */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              End
            </label>

            <div
              className="w-full rounded-lg border py-2 px-4 flex items-center justify-between transition duration-300 focus-within:ring-1 cursor-pointer dark:bg-form-input dark:text-white"
              //onClick={() => endDateRef.current?.setOpen(true)}
            >
              <DatePicker
                ref={endDateRef}
                selected={
                  formData.end_time ? new Date(formData.end_time) : null
                }
                onChange={(date: Date | null) =>
                  handleChangeValue(
                    "end_time",
                    date ? format(date, "yyyy-MM-dd") : ""
                  )
                }
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                className="w-full bg-transparent outline-none"
                minDate={
                  formData.start_time
                    ? new Date(formData.start_time)
                    : selectedProject?.start
                    ? new Date(selectedProject.start)
                    : undefined
                }
                maxDate={
                  selectedProject?.end
                    ? new Date(selectedProject.end)
                    : undefined
                }
                disabled={!formData.start_time}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                openToDate={
                  formData.start_time
                    ? new Date(formData.start_time)
                    : selectedProject?.start
                    ? new Date(selectedProject.start)
                    : new Date()
                }
              />
              <Calendar className="w-5 h-5 text-gray-400 ml-2" />
            </div>
            {errors.end_time && (
              <p className="text-red-500 text-sm mt-1">{errors.end_time}</p>
            )}
          </div>
          <div className="col-span-1">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Status <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                  errors.status
                    ? "border-red-500"
                    : "border-stroke dark:border-form-strokedark"
                }`}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {errors.status && (
              <p className="text-red-500 text-sm mt-1">{errors.status}</p>
            )}
          </div>
          <div className="col-span-1">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Reporting Manager <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="reportingManager"
                value={formData.reportingManager}
                onChange={handleChange}
                className="appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white border-stroke dark:border-form-strokedark"
              >
                <option value="" disabled hidden>Select Manager</option>
                {manager.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {errors.reportingManager && (
              <p className="text-red-500 text-sm mt-1">
                {errors.reportingManager}
              </p>
            )}
          </div>
          <div className="col-span-1 lg:col-span-2 bg-slate-100 rounded-lg p-4">
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-white">
                Select Member
              </label>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-4 mb-4 w-full">
              {totalMembers > 0 && (
                <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-white">
                  <input
                    type="checkbox"
                    className="form-checkbox text-blue-600"
                    checked={isSelectAllChecked}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                  />
                  <span>
                    Select All ({selectedCount} of {allMemberIds.length}{" "}
                    selected)
                  </span>
                </label>
              )}
              <div className="w-full sm:w-auto sm:flex-none sm:ml-auto">
                <input
                  type="text"
                  placeholder="Search by member name or code"
                  value={search}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^\s+/, "");
                    setSearch(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault(); // ⛔ Prevent form submit
                    }
                  }}
                  className="w-full sm:w-64 rounded-md border px-4 py-2 text-sm focus:border-blue-500 focus:ring-blue-200 dark:bg-form-input dark:text-white"
                />
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white">
                  <tr>
                    <th className="px-4 py-2">
                      {/* <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={isSelectAllChecked}
                        onChange={(e) =>
                          handleSelectAllChange(e.target.checked)
                        }
                      /> */}
                    </th>
                    <th className="px-2 sm:px-4 py-2">Member Name</th>
                    <th className="px-2 sm:px-4 py-2">Member Code</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center font-medium py-4">
                        No result found.
                      </td>
                    </tr>
                  ) : (
                    allUsers.map((user) => (
                      <tr
                        key={user.id}
                        className={`${
                          isUserChecked(user.id)
                            ? "bg-blue-50 dark:bg-blue-900/20"
                            : ""
                        } hover:bg-gray-50 dark:hover:bg-gray-800 transition`}
                      >
                        <td className="px-2 sm:px-4 py-2">
                          <input
                            type="checkbox"
                            className="form-checkbox"
                            checked={isUserChecked(user.id)}
                            onChange={() => handleCheckboxChange(user.id)}
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-2 break-words">
                          {user.member_name}
                        </td>
                        <td className="px-2 sm:px-4 py-2 break-words">
                          {user.unique_code}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalMembers > 0 && (
              <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
                  {Math.min(currentPage * PAGE_SIZE, totalMembers)} of{" "}
                  {totalMembers} members
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm rounded border disabled:opacity-50"
                  >
                    &lt;
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-sm rounded border ${
                          page === currentPage
                            ? "bg-gray-300 dark:bg-gray-700"
                            : ""
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm rounded border disabled:opacity-50"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="col-span-1 lg:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-black text-white py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                loading
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:bg-opacity-90"
              }`}
            >
              {survey ? "Update" : "Save"}
              {loading && (
                <span className="h-4 w-4 ml-2 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateSurvey;
